// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

import type { Translator } from "@unilarva/translator";
import type { InitialState } from "./types";
import type { GroupedSongResult, SongMetadata } from "./song-results";
import { displaySongTitle } from "./song-index";
import { getEnabledBooks, resolveCoverUrl } from "./songbook";
import { htmlEncode } from "./generic/object-utils";
import { serializeViewUrl } from "./view-url";

/** Presentation inputs, independent of search form, history, and filter state. */
export type SongResultRenderContext = {
  translator: Translator;
  books: InitialState["books"];
  destinationHashParam?: string;
  queryNorm?: string;
  showCovers?: boolean;
  /** Native disclosures include all content; lazy disclosures are enhanced by the browser wrapper. */
  detailsMode?: "native" | "lazy";
  /** Song pages show common extended metadata without a disclosure or heading-level title. */
  songPage?: boolean;
  /** Origin used to resolve relative audio links, independent of browser globals. */
  origin?: string;
  defaultLanguage?: string;
};

const escape = (value: string): string => htmlEncode(value, true);

/** Renders expanded lyrics, translations, and audio links. */
export function renderSongMetadata(
  metadata: Partial<SongMetadata>,
  context: SongResultRenderContext,
  headingLevel: 2 | 3 = 3,
): string {
  const t = context.translator;
  const text = (value: string | number): string =>
    highlightInsensitive(String(value), context.queryNorm ?? "");
  const section = (key: string, body: string): string =>
    `<section class="sb-search-detail-section">
      <h${headingLevel} data-i18n="${key}">${escape(t.translateKey(key))}</h${headingLevel}>
      ${body}
    </section>`;
  const audioItems = Array.isArray(metadata.audioLinks)
    ? metadata.audioLinks
        .map(a => {
          if (!a || typeof a !== "object") return "";
          const href = typeof a.url === "string" ? a.url.trim() : "";
          let label = href || String(a.title ?? "");
          let safe = false;
          if (href) {
            try {
              const url = new URL(href, context.origin ?? "https://song-result.invalid");
              safe = url.protocol === "http:" || url.protocol === "https:";
              label = url.host === "song-result.invalid" ? label : url.host || label;
            } catch {
              // Preserve malformed labels as text, never as active links.
            }
          }
          if (!label) return "";
          return `<li>${safe ? `<a class="sb-card-link" href="${escape(href)}" target="_blank" rel="nofollow noopener">${escape(label)}</a>` : escape(label)}</li>`;
        })
        .join("\n        ")
    : "";
  const audio = audioItems
    ? section("search.info.audio", `<ul class="sb-card-list">\n        ${audioItems}\n      </ul>`)
    : "";
  const verses = (value: string[][]): string =>
    `<div class="sb-search-poem">
        ${value
          .filter(Array.isArray)
          .map(
            verse =>
              `<div class="sb-search-verse">
          ${verse.map(ln => `<div class="sb-search-line">${text(String(ln ?? ""))}</div>`).join("\n          ")}
        </div>`,
          )
          .join("\n        ")}
      </div>`;
  const lyrics =
    Array.isArray(metadata.lyrics) && metadata.lyrics.length
      ? section("search.info.lyrics", verses(metadata.lyrics))
      : "";
  const translationItems = Array.isArray(metadata.translations)
    ? metadata.translations
        .map(tr => {
          if (!tr || typeof tr !== "object" || !Array.isArray(tr.lyrics)) return "";
          const language = String(tr.language ?? "").trim();
          let languageTag = "";
          // Labels may be prose; only code-shaped, structurally valid tags belong in lang.
          if (/^[a-z]{2,3}(?:-[a-z0-9]{1,8})*$/i.test(language)) {
            try {
              languageTag = Intl.getCanonicalLocales(language)[0] ?? "";
            } catch {
              // Keep an invalid code as a displayed label, not a language declaration.
            }
          }
          return `<div class="sb-search-translation"${languageTag ? ` lang="${escape(languageTag)}"` : ""}>
        ${language ? `<div class="sb-search-translation-lang">${escape(language.toUpperCase())}</div>` : ""}
        ${verses(tr.lyrics)}
      </div>`;
        })
        .join("\n      ")
    : "";
  const translations = translationItems
    ? section("search.info.translations", translationItems)
    : "";
  return [audio, lyrics, translations].filter(Boolean).join("\n    ");
}

/** Renders one grouped song as HTML with native full details by default. */
export function renderGroupedSongResultHtml(
  group: GroupedSongResult,
  context: SongResultRenderContext,
): string {
  const t = context.translator;
  const books = getEnabledBooks(context.books);
  const tags = new Map((context.books.tags ?? []).map(tag => [tag.id, tag]));
  const text = (value: string | number): string =>
    highlightInsensitive(String(value), context.queryNorm ?? "");
  const label = (key: string): string =>
    `<span data-i18n="${key}">${escape(t.translateKey(key))}</span>`;
  const line = (key: string, value: string | number | null | undefined): string =>
    value == null || value === ""
      ? ""
      : `<div class="sb-search-meta-line">
          <span class="sb-search-meta-key">${label(key)}</span>
          <span class="sb-search-meta-value">${text(value)}</span>
        </div>`;
  const immediate = (metadata: Partial<SongMetadata>, identity: boolean): string =>
    [
      ...(identity
        ? [
            line("search.result.title", metadata.title),
            line("search.result.number", metadata.number),
          ]
        : []),
      line("search.result.author", metadata.author),
      line("search.result.chapter", metadata.chapterTitle),
      ...(
        [
          ["offeredTo", "search.detail.offered_to"],
          ["tags", "search.detail.tags"],
          ["extraInfo", "search.detail.extra_info"],
          ["key", "search.detail.key"],
          ["goodKeys", "search.detail.good_keys"],
        ] as const
      ).map(([field, key]) => line(key, metadata[field])),
    ]
      .filter(Boolean)
      .join("\n        ");
  const disclosure = (id: string, metadata: Partial<SongMetadata>): string => {
    const hasExtra =
      metadata.audioLinks?.some(audio => audio?.url || audio?.title) ||
      metadata.lyrics?.some(
        verse => Array.isArray(verse) && verse.some(line => String(line ?? "").trim()),
      ) ||
      metadata.translations?.some(
        translation =>
          Array.isArray(translation?.lyrics) &&
          translation.lyrics.some(
            verse => Array.isArray(verse) && verse.some(line => String(line ?? "").trim()),
          ),
      );
    if (!hasExtra) return "";
    if (context.detailsMode !== "lazy") {
      return `<details class="sb-search-disclosure" data-sb-search-native-details>
      <summary class="sb-expandable-header"><i class="sbi-triangle-right sb-expandable-triangle" aria-hidden="true"></i> ${label("button.more-info")}</summary>
      <div class="sb-search-details">
        <div class="sb-search-details-body" data-sb-search-details-body>
          ${renderSongMetadata(metadata, context)}
        </div>
      </div>
    </details>`;
    }
    return `<div class="sb-search-disclosure">
      <button type="button" class="sb-expandable-header" data-sb-search-open-details="${id}" aria-expanded="false" aria-controls="${id}"><i class="sbi-triangle-right sb-expandable-triangle" aria-hidden="true"></i> ${label("button.more-info")}</button>
      <div id="${id}" class="sb-expandable-section sb-search-details" data-sb-search-details="${id}" data-open="0" aria-hidden="true" inert>
        <div class="sb-expandable-content">
          <div class="sb-search-details-body" data-sb-search-details-body></div>
        </div>
      </div>
    </div>`;
  };
  const tagIcons = (bookId: number, cover: boolean): string => {
    const icons = (books[bookId - 1]?.tag_ids ?? [])
      .map(id => {
        const tag = tags.get(id);
        if (!tag) return "";
        const url = t.translateKey(`DYNAMIC.tag.${id}.iconfile`);
        if (!url) return "";
        const key = `DYNAMIC.tag.${id}.text`;
        return `<img class="sb-tag-icon sb-tag-icon--${cover ? "cover" : "text"}" src="${escape(url)}" data-i18n-alt="${escape(key)}" alt="${escape(t.translateKey(key))}" fetchpriority="high" width="512" height="512" />`;
      })
      .join("\n        ");
    return icons
      ? `<span class="${cover ? "sb-card-tagicons sb-search-cover-tagicons" : "sb-search-tagicons"}" aria-hidden="true">
        ${icons}
      </span>`
      : "";
  };
  const representative = group.representative;
  const commonMetadata = context.songPage
    ? renderSongMetadata(group.commonMetadata, context, 2)
    : "";
  const sharedDetails = context.songPage
    ? commonMetadata
      ? `<div class="sb-search-details" data-sb-song-common-details>
      <div class="sb-search-details-body" data-sb-search-details-body>
        ${commonMetadata}
      </div>
    </div>`
      : ""
    : disclosure(`sb-song-${representative.ref}-shared`, group.commonMetadata);
  const appearances = group.appearances
    .map(({ original: hit, metadata }, index) => {
      const bookTitleKey = `DYNAMIC.book.${hit.bookId}.title`;
      const subtitleKey = `DYNAMIC.book.${hit.bookId}.subtitle`;
      const title = t.translateKey(bookTitleKey);
      const subtitle = t.translateKey(subtitleKey);
      const variantKey = variantLabelKey(hit.variant);
      const variant = variantKey ? label(variantKey) : escape(hit.variant);
      const variantText = variantKey ? t.translateKey(variantKey) : hit.variant;
      const suffix =
        subtitle.trim().toLowerCase() === variantText.trim().toLowerCase()
          ? ""
          : ` <span class="sb-search-variant-muted">(${variant})</span>`;
      const destination = new URLSearchParams();
      if (hit.songId) destination.set(context.destinationHashParam?.trim() || "song", hit.songId);
      const href = serializeViewUrl(
        { view: hit.viewId, lang: t.getLanguage(), hash: destination.toString() },
        { defaultLanguage: context.defaultLanguage },
      );
      const book = books[hit.bookId - 1];
      const coverUrl = book ? resolveCoverUrl(context.books, book) : "";
      const cover =
        context.showCovers && coverUrl
          ? `${context.detailsMode === "lazy" ? '<button type="button"' : `<a href="${escape(coverUrl)}" target="_blank" rel="noopener"`} class="sb-search-meta-cover-wrap" data-sb-search-cover-preview aria-label="${escape(title)}">
              <span class="sb-search-meta-cover-frame">
                <img class="sb-search-meta-cover" src="${escape(coverUrl)}" alt="" aria-hidden="true" />
                ${tagIcons(hit.bookId, true)}
              </span>
            </${context.detailsMode === "lazy" ? "button" : "a"}>`
          : "";
      const newBook = index > 0 && group.appearances[index - 1].original.sourceId !== hit.sourceId;
      return `
      <li class="sb-search-appearance${newBook ? " sb-search-appearance--new-book" : ""}" data-sb-search-appearance="${hit.ref}">
        <div class="sb-search-hit-main-row">
          <a class="sb-search-appearance-main sb-search-hit-link sb-search-hit-main-link" data-view-nav href="${escape(href)}">
            <h3 class="sb-search-appearance-heading"><span data-i18n="${bookTitleKey}">${escape(title)}</span></h3>
            <span class="sb-search-appearance-variant"><span data-i18n="${subtitleKey}">${escape(subtitle)}</span>${suffix}</span>
            ${tagIcons(hit.bookId, false)}
            <div class="sb-search-meta">
              ${immediate(metadata, true)}
              ${line("search.detail.page", hit.page)}
            </div>
          </a>
          <div class="sb-search-hit-right-wrap">
            <a class="sb-btn sb-btn--surface sb-btn--icon sb-search-hit-link" data-view-nav data-sb-view-target="${escape(href)}" href="${escape(href)}"
              data-i18n-title="button.view" title="${escape(t.translateKey("button.view"))}"
              data-i18n-aria-label="button.view" aria-label="${escape(t.translateKey("button.view"))}">
              <i class="sbi-eye" aria-hidden="true"></i> ${label("button.view")}
            </a>
            ${cover}
          </div>
        </div>
        ${disclosure(`sb-song-${representative.ref}-appearance-${hit.ref}`, metadata)}
      </li>`;
    })
    .join("\n");
  const displayTitle = displaySongTitle({
    ...representative,
    title: group.commonMetadata.title || representative.title,
  });
  const title = `${representative.number ? `<span class="sb-search-title-number">${text(`${representative.number}.`)}</span>` : ""}<span class="sb-search-title-text">${text(displayTitle)}</span>`;
  const titleTag = context.songPage ? "p" : "h2";
  const appearancesHeadingTag = context.songPage ? "h2" : "h3";
  return `<article class="sb-search-hit" data-sb-search-hit="${representative.ref}">
    <header class="sb-search-hit-head">
      <${titleTag} class="sb-search-hit-title">${title}</${titleTag}>
      <div class="sb-search-meta">
        ${immediate(group.commonMetadata, false)}
        ${
          group.songId
            ? `<div class="sb-search-meta-line">
          <span class="sb-search-meta-key">ID</span>
          <span class="sb-search-meta-value">${text(group.songId)}</span>
        </div>`
            : ""
        }
      </div>
    </header>
    ${sharedDetails}
    <${appearancesHeadingTag} class="sb-search-appearances-heading">${label("search.result.find-in")}</${appearancesHeadingTag}>
    <ul class="sb-search-appearances">
      ${appearances}
    </ul>
  </article>`;
}

/** Returns the established localized variant label key, or null for custom variants. */
export function variantLabelKey(variant: string): string | null {
  const value = variant.trim().toLowerCase();
  return ["default", "lyrics", "bassclef", "charango"].includes(value)
    ? `search.variant.${value}`
    : null;
}

/** Highlights normalized query matches while escaping all original text. */
export function highlightInsensitive(original: string, queryNorm: string): string {
  const src = String(original ?? "");
  const q = queryNorm.trim();
  if (!src || !q) return escape(src);
  const chars: string[] = [];
  const indexMap: number[] = [];
  for (let i = 0; i < src.length; i++) {
    const folded = src[i]
      .normalize("NFD")
      .replace(/\p{M}+/gu, "")
      .toLowerCase();
    for (const ch of folded) {
      chars.push(ch);
      indexMap.push(i);
    }
  }
  const normalized = chars.join("");
  const ranges: Array<{ start: number; end: number }> = [];
  let from = 0;
  while (from <= normalized.length - q.length) {
    const index = normalized.indexOf(q, from);
    if (index < 0) break;
    const start = indexMap[index];
    const end = indexMap[index + q.length - 1] + 1;
    const last = ranges[ranges.length - 1];
    if (last && start <= last.end) last.end = Math.max(last.end, end);
    else ranges.push({ start, end });
    from = index + Math.max(1, q.length);
  }
  let output = "";
  let position = 0;
  for (const range of ranges) {
    output += escape(src.slice(position, range.start));
    output += `<mark>${escape(src.slice(range.start, range.end))}</mark>`;
    position = range.end;
  }
  return output + escape(src.slice(position));
}
